import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { renderBulletOrEmpty } from 'components/molecules/bidding_summary_bullet_list';
import { NAO_INFORMADO } from 'components/molecules/bidding_summary_constants';
import ExternalRoutes from 'services/external_routes';
import { formatIsoDateBr } from 'services/format_iso_date_br';

import type {
  BiddingSummaryData,
  PossibilidadeQuestionamentoResumo,
} from 'store/modules/bidding_summary_store/types';

interface BiddingSummaryQuestionamentosProps {
  questionamentos?: BiddingSummaryData['possibilidades_questionamentos'];
  // Dados da licitação para montar o caso no Atendimento do Dr. Licita
  summary?: BiddingSummaryData;
  biddingId?: number;
}

type PecaDoQuestionamento = 'impugnacao' | 'esclarecimento';

// O caso que o Atendimento do Dr. Licita recebe no campo da conversa: o
// questionamento clicado e os fatos da licitação, para o usuário conferir e
// completar antes de enviar. Os prazos vêm do resumo, extraídos por IA.
const montarCaso = (
  peca: PecaDoQuestionamento,
  item: PossibilidadeQuestionamentoResumo,
  summary?: BiddingSummaryData,
  biddingId?: number,
): string => {
  const linhas = [
    peca === 'impugnacao'
      ? 'Quero impugnar o edital neste ponto.'
      : 'Quero pedir esclarecimento sobre este ponto do edital.',
    '',
  ];
  const fato = (rotulo: string, valor?: string | null) => {
    if (valor?.trim()) linhas.push(`- ${rotulo}: ${valor.trim()}`);
  };
  // Com o id, o Dr. Licita busca na plataforma o cadastro e o resumo da licitação
  fato('Id ConLicitação', biddingId ? String(biddingId) : '');
  fato('Licitação', summary?.numero_licitacao);
  fato('Órgão', summary?.orgao_responsavel);
  fato('Objeto', summary?.objeto);
  const sessao = formatIsoDateBr(summary?.data_sessao_publica);
  fato(
    'Sessão pública',
    sessao
      ? `${sessao}${summary?.horario_sessao_publica ? ` às ${summary.horario_sessao_publica}` : ''}`
      : '',
  );
  fato('Portal', summary?.portal_realizacao);
  const prazo =
    peca === 'impugnacao'
      ? summary?.prazos_importantes?.limite_impugnacao
      : summary?.prazos_importantes?.prazo_esclarecimentos;
  fato(
    peca === 'impugnacao'
      ? 'Limite para impugnação (do resumo do edital, extraído por IA; confirme)'
      : 'Prazo para esclarecimentos (do resumo do edital, extraído por IA; confirme)',
    prazo,
  );
  fato('Ponto do edital', item.tema);
  fato('Por que questionar', item.justificativa);
  if (item.trecho_edital?.trim()) {
    linhas.push('', 'Trecho do edital:', `> ${item.trecho_edital.trim().replace(/\n+/g, '\n> ')}`);
  }
  return linhas.join('\n');
};

// O botão abre outra aba: o caso viaja pelo localStorage (mesma origem),
// numa chave de uso único que a página do chat lê e apaga
const abrirNoAtendimento = (caso: string) => {
  const chave = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  try {
    window.localStorage.setItem(`drlicita:caso:${chave}`, caso);
  } catch {
    // Sem armazenamento: abre o Atendimento mesmo assim, sem o caso
  }
  window.open(`/dr_licita/chat?mode=atendimento&caso=${chave}`, '_blank', 'noopener');
};

const formatTipoQuestionamento = (tipo?: string): string => {
  if (!tipo?.trim()) return 'Questionamento';
  return tipo
    .split('_')
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase())
    .join(' ');
};

const BiddingSummaryQuestionamentos: React.FC<BiddingSummaryQuestionamentosProps> = ({
  questionamentos,
  summary,
  biddingId,
}) => {
  const [openTrecho, setOpenTrecho] = useState<Record<number, boolean>>({});
  const profile = useSelector((state: any) => state?.storage?.profile?.profile);
  // Quem pode entrar no chat do Dr. Licita (alfa, com o serviço) e tem o
  // Atendimento ligado abre a conversa já com o caso; os demais seguem no
  // gerador de documentos
  const atendimento = Boolean(
    profile?.alfa_tester && profile?.can_use_dr_licita && profile?.dr_licita_atendimento,
  );

  const { legacyList, structuredList } = useMemo(() => {
    if (!Array.isArray(questionamentos)) {
      return {
        legacyList: [] as string[],
        structuredList: [] as Array<
          NonNullable<BiddingSummaryData['possibilidades_questionamentos']>[number]
        >,
      };
    }

    const legacy = questionamentos
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);

    const structured = questionamentos.filter(
      (item): item is NonNullable<BiddingSummaryData['possibilidades_questionamentos']>[number] =>
        Boolean(item) && typeof item === 'object',
    );

    return { legacyList: legacy, structuredList: structured };
  }, [questionamentos]);

  if (!structuredList.length) {
    return <>{renderBulletOrEmpty('questionamentos', legacyList)}</>;
  }

  return (
    <div className="bidding-summary-questionamentos">
      {structuredList.map((item, index) => {
        if (!item || typeof item === 'string') return null;
        const tipo = formatTipoQuestionamento(item.tipo);
        const tema = item.tema?.trim() || NAO_INFORMADO;
        const justificativa = item.justificativa?.trim() || NAO_INFORMADO;
        const trecho = item.trecho_edital?.trim();
        const isTrechoOpen = Boolean(openTrecho[index]);

        return (
          // A lista vem pronta do resumo e não muda de ordem
          // eslint-disable-next-line react/no-array-index-key
          <div key={`questionamento-card-${index}`} className="bidding-summary-questionamento-card">
            <span className="bidding-summary-questionamento-tag">{tipo}</span>
            <h5 className="bidding-summary-questionamento-title mb-2">{tema}</h5>
            <p className="mb-3">{justificativa}</p>

            {trecho ? (
              <div className="bidding-summary-questionamento-trecho">
                <button
                  type="button"
                  className="btn btn-light btn-block d-flex justify-content-between align-items-center text-left border"
                  onClick={() => setOpenTrecho((prev) => ({ ...prev, [index]: !prev[index] }))}
                  aria-expanded={isTrechoOpen}
                >
                  <span className="font-weight-bold">Trecho do edital</span>
                  <i className="material-icons">{isTrechoOpen ? 'expand_less' : 'expand_more'}</i>
                </button>
                {isTrechoOpen ? (
                  <div className="bidding-summary-questionamento-trecho-body border border-top-0 p-3">
                    {trecho}
                  </div>
                ) : null}
              </div>
            ) : null}

            {atendimento ? (
              <div className="bidding-summary-questionamentos-actions d-flex flex-wrap d-print-none mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mr-2"
                  onClick={() =>
                    abrirNoAtendimento(montarCaso('esclarecimento', item, summary, biddingId))
                  }
                >
                  Pedir esclarecimento
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    abrirNoAtendimento(montarCaso('impugnacao', item, summary, biddingId))
                  }
                >
                  Impugnar este item
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
      {legacyList.length ? (
        <div className="mt-2">{renderBulletOrEmpty('questionamentos-legacy', legacyList)}</div>
      ) : null}
      {!atendimento ? (
        <div className="bidding-summary-questionamentos-actions d-flex flex-wrap d-print-none">
          <Link
            to={ExternalRoutes.dr_licita_create_document}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary mr-2"
          >
            Pedir esclarecimentos
          </Link>
          <Link
            to={ExternalRoutes.dr_licita_create_document}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary"
          >
            Impugnar
          </Link>
        </div>
      ) : null}
    </div>
  );
};

BiddingSummaryQuestionamentos.defaultProps = {
  questionamentos: undefined,
  summary: undefined,
  biddingId: undefined,
};

export default BiddingSummaryQuestionamentos;
